// Re-export enums from the single source of truth
export {
  AttributionSource,
  TripleWhaleMetric,
  GoogleAnalyticsMetric,
  GoogleAdsMetric,
  TiktokMetric,
  PinterestMetric,
  MetaMetric,
  MarathonMetric,
  ShopifyMetric,
} from "~/ui-rtk/api/types/custom-attribution-enums"

import {
  AttributionSource,
  TripleWhaleMetric,
  MetaMetric,
  MarathonMetric,
  ShopifyMetric,
  GoogleAnalyticsMetric,
  GoogleAdsMetric,
  TiktokMetric,
  PinterestMetric,
} from "~/ui-rtk/api/types/custom-attribution-enums"

// Centralized metric label mappings
export const METRIC_LABELS = {
  [AttributionSource.GOOGLE_ANALYTICS]: {
    [GoogleAnalyticsMetric.LAST_CLICK_REVENUE]: "last click revenue",
  },
  [AttributionSource.GOOGLE_ADS]: {
    [GoogleAdsMetric.SEVEN_DAY_CLICK_1D_VIEW]:
      "Platform-reported click-thru revenue",
  },
  [AttributionSource.TIKTOK]: {
    [TiktokMetric.SEVEN_DAY_CLICK_1D_VIEW]: "seven day click 1d view",
  },
  [AttributionSource.PINTEREST]: {
    [PinterestMetric.THIRTY_DAY_CLICK_30D_ENGAGEMENT_1D_VIEW]:
      "30d click / 30d engagement / 1d view",
    [PinterestMetric.THIRTY_DAY_CLICK]: "30d click",
    [PinterestMetric.THIRTY_DAY_ENGAGEMENT]: "30d engagement",
    [PinterestMetric.ONE_DAY_VIEW]: "1d view",
  },
  [AttributionSource.META]: {
    [MetaMetric.ONE_DAY_ENGAGED_VIEW]: "1d engaged view",
    [MetaMetric.SEVEN_DAY_CLICK]: "7d click",
    [MetaMetric.ONE_DAY_VIEW]: "1d view",
    [MetaMetric.ONE_DAY_CLICK]: "1d click",
    [MetaMetric.INCREMENTALITY]: "incrementality",
  },
  [AttributionSource.MARATHON]: {
    [MarathonMetric.BRAND_VALUE_30D]: "30 days brand value",
    [MarathonMetric.BRAND_VALUE_60D]: "60 days brand value",
    [MarathonMetric.BRAND_VALUE_90D]: "90 days brand value",
    [MarathonMetric.BRAND_VALUE_180D]: "180 days brand value",
    [MarathonMetric.REVENUE_FROM_SEARCH]: "revenue from search",
  },
  [AttributionSource.SHOPIFY]: {
    [ShopifyMetric.LAST_CLICK_REVENUE]: "last click revenue",
    [ShopifyMetric.FIRST_CLICK_REVENUE]: "first click revenue",
  },
  [AttributionSource.YOUTUBE_ADS]: {},
  // Triple Whale metrics (display labels; used when source is channel + Triple Whale)
  TRIPLE_WHALE: {
    [TripleWhaleMetric.TRIPLE_WHALE_LINEAR]: "Linear Total Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_LAST_PLATFORM_CLICK]:
      "Last Platform Click Total Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_FULL_FIRST_CLICK]:
      "Full First Click Total Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_FULL_LAST_CLICK]:
      "Full Last Click Total Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_LINEAR_ALL]: "Linear All Total Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_LINEAR_NC]: "Linear NC Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_LAST_PLATFORM_CLICK_NC]:
      "Last Platform Click NC Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_FULL_FIRST_CLICK_NC]:
      "Full First Click NC Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_FULL_LAST_CLICK_NC]:
      "Full Last Click NC Revenue",
    [TripleWhaleMetric.TRIPLE_WHALE_LINEAR_ALL_NC]: "Linear All NC Revenue",
  },
} as const

// Helper function to get metric label
export const getMetricLabel = (source: string, metric: string): string => {
  const labelsMap = METRIC_LABELS as Record<string, Record<string, string>>
  const sourceLabels = labelsMap[source] ?? {}
  if (sourceLabels[metric]) return sourceLabels[metric]
  const tripleWhaleLabels = labelsMap.TRIPLE_WHALE ?? {}
  if (tripleWhaleLabels[metric]) return tripleWhaleLabels[metric]
  return metric.replace(/_/g, " ").toLowerCase()
}

// Synthetic source values for "Triple Whale" option per channel (map to channel source on submit)
export const TRIPLE_WHALE_SOURCE_PREFIX = "TRIPLE_WHALE_"
export const TRIPLE_WHALE_SOURCE_META = "TRIPLE_WHALE_META"
export const TRIPLE_WHALE_SOURCE_TIKTOK = "TRIPLE_WHALE_TIKTOK"
export const TRIPLE_WHALE_SOURCE_GOOGLE_ADS = "TRIPLE_WHALE_GOOGLE_ADS"
export const TRIPLE_WHALE_SOURCE_YOUTUBE_ADS = "TRIPLE_WHALE_YOUTUBE_ADS"
export const TRIPLE_WHALE_SOURCE_PINTEREST = "TRIPLE_WHALE_PINTEREST"

/** Map Triple Whale synthetic source to API attribution source for submit */
export const tripleWhaleSourceToAttributionSource: Record<
  string,
  AttributionSource
> = {
  [TRIPLE_WHALE_SOURCE_META]: AttributionSource.META,
  [TRIPLE_WHALE_SOURCE_TIKTOK]: AttributionSource.TIKTOK,
  [TRIPLE_WHALE_SOURCE_GOOGLE_ADS]: AttributionSource.GOOGLE_ADS,
  [TRIPLE_WHALE_SOURCE_YOUTUBE_ADS]: AttributionSource.YOUTUBE_ADS,
  [TRIPLE_WHALE_SOURCE_PINTEREST]: AttributionSource.PINTEREST,
}

export function isTripleWhaleSource(source: string): boolean {
  return source.startsWith(TRIPLE_WHALE_SOURCE_PREFIX)
}

/** Resolve form source to API source (Triple Whale synthetic -> channel source) */
export function resolveWeightSourceForApi(source: string): string {
  return tripleWhaleSourceToAttributionSource[source] ?? source
}

/** When loading from API: if metric is Triple Whale, use synthetic source so UI shows "Triple Whale" */
export function weightSourceForForm(apiSource: string, metric: string): string {
  if (metric.startsWith("TRIPLE_WHALE_")) {
    const key = `TRIPLE_WHALE_${apiSource}`
    if (key in tripleWhaleSourceToAttributionSource) return key
  }
  return apiSource
}

// Helper function to get source label
export const getSourceLabel = (source: string): string => {
  if (isTripleWhaleSource(source)) return "Triple Whale"
  return source.replace(/_/g, " ").toLowerCase()
}

/** For display only: show "Triple Whale" when metric is Triple Whale (API returns channel as source). */
export function getSourceLabelForDisplay(
  source: string,
  metric: string,
): string {
  if (metric.startsWith("TRIPLE_WHALE_")) return "Triple Whale"
  return getSourceLabel(source)
}
